import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { analyseRecordSensitivity } from './supportResources';
import { logActivity } from '../../lib/activityLog';

/**
 * Support organisations as plain white rows on a care-wash surface.
 * Phone numbers are the evidence layer: mono, care-ink, tappable.
 *
 * Props:
 *   orgs — [{ name, description?, phone?, url? }]
 *   onOrgClick?(org, 'phone' | 'website')
 *   testId?
 */
export const SupportOrgList = ({ orgs = [], onOrgClick, testId }) => {
  if (!orgs.length) return null;
  return (
    <ul className="space-y-2" data-testid={testId}>
      {orgs.map((o) => (
        <li key={o.name} className="rounded-md bg-surface px-4 py-3">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <span className="font-semibold text-ink text-[15px]">{o.name}</span>
            <div className="flex items-baseline gap-4 flex-wrap">
              {o.phone && (
                <a
                  href={`tel:${o.phone.replace(/\s/g, '')}`}
                  onClick={() => onOrgClick?.(o, 'phone')}
                  className="font-mono text-[15px] tracking-[0.04em] text-care-ink hover:underline"
                  data-testid={`support-call-${o.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {o.phone}
                </a>
              )}
              {o.url && (
                <a
                  href={o.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => onOrgClick?.(o, 'website')}
                  className="text-sm text-ink-55 underline underline-offset-2 hover:text-ink"
                >
                  Website
                </a>
              )}
            </div>
          </div>
          {o.description && (
            <p className="text-sm text-ink-55 leading-relaxed mt-0.5">{o.description}</p>
          )}
        </li>
      ))}
    </ul>
  );
};

/**
 * Emotional-safety notice at the top of a sensitive record's detail view.
 * Opens with the eyebrow and one plain sentence; "Show support" reveals the
 * support organisations resolved server-side for this record.
 *
 * Props:
 *   record — full record object (expects sensitive, support_orgs)
 *   onDismissed?() — called when the user closes the notice
 */
export const SupportHint = ({ record, onDismissed }) => {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);

  const { sensitive, orgs, label } = analyseRecordSensitivity(record);

  // Log when the warning becomes visible. Fires once per record render.
  useEffect(() => {
    if (sensitive && !hidden) {
      logActivity('sensitive_record_warning_shown', {
        category: label || 'sensitive',
      });
    }
    // We intentionally use `record?.id` to refire on record change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sensitive, record?.id]);

  if (!sensitive || hidden) return null;

  const handleDismiss = () => {
    setHidden(true);
    logActivity('sensitive_record_warning_dismissed', { category: label || 'sensitive' });
    onDismissed?.();
  };

  const handleToggle = () => {
    setOpen((v) => {
      const next = !v;
      if (next) {
        logActivity('sensitive_record_warning_expanded', { category: label || 'sensitive' });
      }
      return next;
    });
  };

  const handleSupportClick = (org, type) => {
    logActivity('support_org_clicked', {
      organisation: org.name,
      category: type, // 'phone' | 'website'
    });
  };

  const mentions = label ? label.toLowerCase() : 'sensitive content';

  return (
    <section
      className="rounded-lg bg-care-wash p-5 space-y-4"
      aria-label="Before you open this"
      data-testid="record-support-hint"
    >
      <div className="space-y-2">
        <p className="pd-mono text-care-ink">Before you open this</p>
        <p className="text-[17px] leading-relaxed text-ink">
          This record mentions {mentions}. You can read it at your own pace, and stop whenever you want.
        </p>
      </div>

      {open && (
        <SupportOrgList orgs={orgs} onOrgClick={handleSupportClick} testId="support-hint-orgs" />
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={handleToggle}
          className="bg-surface hover:bg-surface/70"
          data-testid="support-hint-toggle"
        >
          {open ? 'Hide support' : 'Show support'}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleDismiss}
          className="text-care-ink hover:text-care-ink hover:bg-surface/60"
          data-testid="support-hint-dismiss"
        >
          Dismiss
        </Button>
        {!open && orgs.length > 0 && (
          <span className="text-sm text-ink-55">
            {orgs.length} organisation{orgs.length === 1 ? '' : 's'} you can call
          </span>
        )}
      </div>
    </section>
  );
};

export default SupportHint;
